// apps/web/src/components/primitives/FormIsian.tsx
/**
 * The form every edit form sits in (spec 2026-09-30-sapuan-isian, 3.6 and 3.9).
 *
 * Enter follows one rule (`aturanEnter`): in a field it moves to the next field, on the last
 * field it saves, in a form of one field it saves, and where Enter belongs to the control (an
 * open list, a textarea, the tag box with text, a button, a link) it is left alone. A grid
 * that keeps its own Enter (Mode Salin Buku) passes `aturEnter={false}`. Saving from Enter
 * clicks the form's first submit button, as the browser's implicit submission does, so the
 * button's own guards hold (EditFooter's Simpan while a save runs, a verb disabled offline);
 * only a form without one is submitted directly. An Enter left alone in a text field
 * (Shift+Enter, a field outside the walk) is still kept from the browser, whose implicit
 * submission would save from a middle field; an input method's Enter never is.
 *
 * A save with problems shows "2 isian perlu diperbaiki:" at the top with one link per field,
 * in the order of the form, and moves the focus there. The list is read from the page (every
 * field that says aria-invalid="true", its label and its sentence), so react-hook-form forms
 * and forms with their own state get it alike; it follows the fields as they are corrected and
 * goes when none is left. The browser's own submission never happens, and a submit while the
 * form says `aria-busy` (a save running) is ignored. A line holds its field's id, and the
 * field is found on the page when the line is used: a field put back under the same id (a row
 * re-keyed) is the one that takes the focus, never the element that went. A line keeps its
 * sentence while its field hides it (a NIK's or phone's count sentence waits while the field
 * has the focus, `pesanTetap`): it does not turn into "Periksa isian ini." when its link puts
 * the focus there.
 *
 * The summary takes the focus only once the submit has settled: react-hook-form's
 * handleSubmit focuses the first field with a problem, at once and again in a setTimeout,
 * and that must not win over the heading. `onSubmit` should return handleSubmit's promise;
 * the heading is focused a turn of the event loop after it settles. A caller that drops it
 * (`void handleSubmit(…)(event)`) still works for a resolver that answers without waiting
 * (Zod): the heading then waits two turns after the submit. This needs nothing from each
 * form (no `shouldFocusError: false` to remember), which is why it is done here. Once the
 * summary is empty and the claim has lapsed, the form stops following its fields: a form that
 * stays on the page after a good save shows no summary until the next save.
 *
 * While a pointer is pressed nothing above the form's buttons moves (the test sweep of 2026-10-04,
 * B3: pressing the button left the last field with a problem, its check on leaving cleared it, the
 * summary collapsed, the button moved up before the release, and the click missed): the
 * summary keeps its rows and the form's `onBlur` waits until the press is let go and its click
 * dispatched (`isian/tekan`). One click on Lanjut or Simpan lands where it was aimed.
 *
 * A form of one field (`satuIsian`) shows no summary: its field's own sentence says it all, and
 * the focus goes to the field. Mode Salin Buku asks for the summary on one wrapping line
 * (`sebaris`), so the form still fits the screen with its footer.
 *
 * Keys and submits from a dialog the form opens in a portal reach this form through React's
 * tree, not the page's: they are left to the dialog.
 */
import {
  type FocusEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { cn } from '@/lib/utils'
import {
  fokusKontrol,
  indeksStop,
  kumpulkanGalat,
  sasaranEnter,
  stopIsian,
  tombolKirim,
} from './isian/dom'
import { aturanEnter, cegahKirimAsli } from './isian/enter'
import { pasangRef } from './isian/ref'
import {
  type ItemRingkasan,
  itemRingkasan,
  judulRingkasan,
  pesanTetap,
  samaRingkasan,
} from './isian/ringkasan'
import { pasangTekan, setelahTekan } from './isian/tekan'

/** A submit's claim on the focus lapses once the person types again, or after this long. */
const MASA_FOKUS_MS = 1500

export interface FormIsianProps {
  /** Return react-hook-form's handleSubmit promise: the summary's focus waits for it. */
  onSubmit: (event: FormEvent<HTMLFormElement>) => unknown
  children: ReactNode
  /** The form holds one field: Enter in it saves. */
  satuIsian?: boolean | undefined
  /** False for a grid that keeps its own Enter (Mode Salin Buku). */
  aturEnter?: boolean | undefined
  /** False where the focus stays with the first field that has a problem (Mode Salin Buku). */
  fokusRingkasan?: boolean | undefined
  /** Raised by a form that checks without a submit event (Mode Salin Buku's Enter). */
  mintaRingkasan?: number | undefined
  onKeyDown?: ((event: KeyboardEvent<HTMLFormElement>) => void) | undefined
  /**
   * A field left: its check. While a pointer is pressed it runs once the press is let go
   * (after the click): read `event.target`, never `event.currentTarget`.
   */
  onBlur?: ((event: FocusEvent<HTMLFormElement>) => void) | undefined
  className?: string | undefined
  'aria-label'?: string | undefined
  /** A save runs: a submit (Enter, a click) is ignored until it ends. */
  'aria-busy'?: boolean | undefined
  /** The form element, for a caller that moves the focus inside it (the person page). */
  ref?: Ref<HTMLFormElement> | undefined
  /** The summary heading's level: 'h3' inside a dialog whose title is the h2. */
  judul?: 'h2' | 'h3' | undefined
  /** The summary on one wrapping line, its title and links side by side (Mode Salin Buku). */
  sebaris?: boolean | undefined
  /** Which step a form of a flow is (the second step's code form: 'kode', 'pasang'). */
  'data-langkah'?: string | undefined
}

/** The field a summary line names, as it is on the page now. */
function kontrolDari(dok: Document, kunci: string): HTMLElement | undefined {
  const el = dok.getElementById(kunci)
  return el instanceof HTMLElement ? el : undefined
}

/** One problem of the summary: a link that puts the focus on its field. */
function TautanRingkasan({ item, sebaris = false }: { item: ItemRingkasan; sebaris?: boolean }) {
  return (
    <a
      href={`#${item.kunci}`}
      onClick={(event) => {
        event.preventDefault()
        fokusKontrol(kontrolDari(event.currentTarget.ownerDocument, item.kunci))
      }}
      className={cn(
        'inline-flex items-center text-table text-text underline decoration-border underline-offset-4',
        'hover:text-secondary hover:decoration-secondary focus-settle pointer-coarse:min-h-11',
        sebaris ? 'min-h-6' : 'min-h-9',
      )}
    >
      {item.teks}
    </a>
  )
}

function janji(nilai: unknown): nilai is PromiseLike<unknown> {
  return (
    typeof nilai === 'object' &&
    nilai !== null &&
    typeof (nilai as { then?: unknown }).then === 'function'
  )
}

export function FormIsian({
  onSubmit,
  children,
  satuIsian = false,
  aturEnter = true,
  fokusRingkasan = true,
  mintaRingkasan = 0,
  onKeyDown,
  onBlur,
  className,
  'aria-label': ariaLabel,
  'aria-busy': ariaBusy,
  ref,
  judul: Judul = 'h2',
  sebaris = false,
  'data-langkah': dataLangkah,
}: FormIsianProps) {
  const formRef = useRef<HTMLFormElement | null>(null)
  const refForm = useMemo(() => pasangRef(formRef, ref), [ref])
  const judulRef = useRef<HTMLHeadingElement>(null)
  const judulId = useId()
  const [baris, setBaris] = useState<ItemRingkasan[]>([])
  // The rows as last read, for the focus of a form of one field (its first field with a problem).
  const barisRef = useRef<ItemRingkasan[]>([])
  barisRef.current = baris
  // Each listed field's last sentence, kept while the field hides it.
  const pesanLama = useRef<ReadonlyMap<string, string>>(new Map())
  const [giliranFokus, setGiliranFokus] = useState(0)
  // After a save was tried the summary follows the fields; a fresh try claims the focus once
  // the submit has settled (`reda`), until the person types or the claim's time is up.
  const aktif = useRef(false)
  const mauFokus = useRef(false)
  const reda = useRef(true)
  const dalamMasa = useRef(false)
  const jumlah = useRef(0)
  const giliranKirim = useRef(0)
  const jam = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cobaFokus = useCallback(() => {
    if (!mauFokus.current || !reda.current || jumlah.current === 0) return
    mauFokus.current = false
    setGiliranFokus((n) => n + 1)
  }, [])

  // Nothing left to fix and the save's claim over: stop following the fields.
  const lepasJikaKosong = useCallback(() => {
    if (!dalamMasa.current && reda.current && jumlah.current === 0) aktif.current = false
  }, [])

  const baca = useCallback(() => {
    const form = formRef.current
    if (form === null || !aktif.current) return
    const tetap = pesanTetap(kumpulkanGalat(form), pesanLama.current)
    pesanLama.current = tetap.ingat
    const items = itemRingkasan(tetap.kontrol)
    jumlah.current = items.length
    setBaris((lama) => (samaRingkasan(lama, items) ? lama : items))
    cobaFokus()
    lepasJikaKosong()
  }, [cobaFokus, lepasJikaKosong])

  const minta = useCallback(
    (selesai?: PromiseLike<unknown>) => {
      giliranKirim.current += 1
      const nomor = giliranKirim.current
      aktif.current = true
      mauFokus.current = true
      reda.current = false
      dalamMasa.current = true
      if (jam.current !== null) clearTimeout(jam.current)
      jam.current = setTimeout(() => {
        mauFokus.current = false
        dalamMasa.current = false
        lepasJikaKosong()
      }, MASA_FOKUS_MS)
      // A turn of the event loop: past a setTimeout scheduled before it (react-hook-form's).
      const giliran = () => new Promise<void>((lanjut) => setTimeout(lanjut, 0))
      const tunggu =
        selesai === undefined
          ? giliran().then(giliran)
          : Promise.resolve(selesai).then(giliran, giliran)
      void tunggu.then(() => {
        if (nomor !== giliranKirim.current) return
        reda.current = true
        cobaFokus()
        lepasJikaKosong()
      })
      baca()
    },
    [baca, cobaFokus, lepasJikaKosong],
  )

  // A field's state changing (its sentence appears, aria-invalid flips) is read again; while a
  // pointer is pressed, once it is let go, so the summary never moves the button being pressed.
  useEffect(() => {
    const form = formRef.current
    if (form === null) return
    pasangTekan(form.ownerDocument)
    const pengamat = new MutationObserver(() => setelahTekan(baca))
    pengamat.observe(form, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-invalid'],
    })
    return () => {
      pengamat.disconnect()
      if (jam.current !== null) clearTimeout(jam.current)
    }
  }, [baca])

  useEffect(() => {
    if (giliranFokus === 0 || !fokusRingkasan) return
    // One field: no summary to focus; the field itself, with its sentence.
    if (satuIsian) {
      const pertama = barisRef.current[0]
      const form = formRef.current
      if (pertama !== undefined && form !== null) {
        fokusKontrol(kontrolDari(form.ownerDocument, pertama.kunci))
      }
      return
    }
    judulRef.current?.focus()
  }, [giliranFokus, fokusRingkasan, satuIsian])

  useEffect(() => {
    if (mintaRingkasan > 0) minta()
  }, [mintaRingkasan, minta])

  /** The event happened in this form's own page, not in a dialog it renders in a portal. */
  const diSini = (target: EventTarget) => {
    const form = formRef.current
    return form !== null && target instanceof Node && form.contains(target)
  }

  const kirim = (event: FormEvent<HTMLFormElement>) => {
    if (!diSini(event.target)) return
    event.preventDefault()
    if (ariaBusy === true) return
    const hasil = onSubmit(event)
    minta(janji(hasil) ? hasil : undefined)
  }

  const tekan = (event: KeyboardEvent<HTMLFormElement>) => {
    if (!diSini(event.target)) return
    onKeyDown?.(event)
    if (event.key !== 'Enter') {
      mauFokus.current = false
      return
    }
    const form = formRef.current
    if (!aturEnter || form === null || !(event.target instanceof HTMLElement)) return
    const stop = stopIsian(form)
    const indeks = indeksStop(stop, event.target)
    const sasaran = sasaranEnter(event)
    const aksi = aturanEnter(sasaran, { indeks, jumlah: stop.length }, satuIsian)
    if (aksi === 'biarkan') {
      if (cegahKirimAsli(sasaran)) event.preventDefault()
      return
    }
    event.preventDefault()
    if (aksi === 'lanjut') {
      fokusKontrol(stop[indeks + 1])
      return
    }
    const tombol = tombolKirim(form)
    if (tombol !== null) tombol.click()
    else form.requestSubmit()
  }

  return (
    <form
      ref={refForm}
      noValidate
      onSubmit={kirim}
      onKeyDown={tekan}
      onBlur={onBlur === undefined ? undefined : (event) => setelahTekan(() => onBlur(event))}
      aria-label={ariaLabel}
      aria-busy={ariaBusy}
      data-langkah={dataLangkah}
      className={className}
    >
      {baris.length > 0 && !satuIsian ? (
        <section
          data-ringkasan=""
          data-sebaris={sebaris ? '' : undefined}
          aria-labelledby={judulId}
          className={cn(
            'border-l-2 border-text bg-surface',
            sebaris
              ? 'flex flex-wrap items-baseline gap-x-4 gap-y-0 px-3 py-1'
              : 'flex flex-col gap-2 px-4 py-3',
          )}
        >
          <Judul
            id={judulId}
            ref={judulRef}
            tabIndex={-1}
            className={cn(
              'font-semibold text-text outline-none',
              sebaris ? 'text-label' : 'text-body',
            )}
          >
            {judulRingkasan(baris.length)}
          </Judul>
          {sebaris ? (
            baris.map((item) => <TautanRingkasan key={item.kunci} item={item} sebaris />)
          ) : (
            <ul className="flex flex-col gap-1">
              {baris.map((item) => (
                <li key={item.kunci}>
                  <TautanRingkasan item={item} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
      {children}
    </form>
  )
}
